import {
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  type UserCredential,
  type User,
} from 'firebase/auth';

import { getFirebase } from '@/providers/firebase';

/**
 * Result of checking organization access
 */
export type CheckAccessResult = {
  authorized: boolean;
  organizations: Array<string>;
  isAdmin?: boolean;
};

/**
 * Registers the user with the server backend after Firebase authentication
 *
 * @param idToken - Firebase ID token
 */
const registerWithServer = async (idToken: string): Promise<void> => {
  const response = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken }),
  });

  if (!response.ok) {
    console.error('Failed to register with server:', response.statusText);
  }
};

/**
 * Logs in a user with email and password using Firebase authentication
 *
 * @param email - User's email address
 * @param password - User's password
 *
 * @returns Promise resolving to Firebase UserCredential
 */
export const login = async (
  email: string,
  password: string,
): Promise<UserCredential> => {
  const firebase = getFirebase();
  const credential = await signInWithEmailAndPassword(
    firebase.auth,
    email,
    password,
  );

  // Register with server to ensure user exists in database
  const idToken = await credential.user.getIdToken();
  await registerWithServer(idToken);

  return credential;
};

/**
 * Logs out the current user from Firebase
 *
 * @returns Promise that resolves when logout completes
 */
export const logout = async (): Promise<void> => {
  const firebase = getFirebase();
  return signOut(firebase.auth);
};

/**
 * Gets the currently logged in Firebase user
 *
 * @returns The current User object or null if not logged in
 */
export const getCurrentUser = (): User | null => {
  const firebase = getFirebase();
  return firebase.auth.currentUser;
};

/**
 * Creates a new user account with email and password using Firebase authentication
 *
 * @param email - User's email address
 * @param password - User's password
 *
 * @returns Promise resolving to Firebase UserCredential
 */
export const signup = async (
  email: string,
  password: string,
): Promise<UserCredential> => {
  const firebase = getFirebase();
  const credential = await createUserWithEmailAndPassword(
    firebase.auth,
    email,
    password,
  );

  // Register with server to create user in database
  const idToken = await credential.user.getIdToken();
  await registerWithServer(idToken);

  return credential;
};

/**
 * Logs in a user with Google using Firebase popup authentication
 *
 * @throws Error with friendly message if account exists with different credential
 *
 * @returns Promise resolving to Firebase UserCredential
 */
export const loginWithGoogle = async (): Promise<UserCredential> => {
  const firebase = getFirebase();
  const provider = new GoogleAuthProvider();

  try {
    const credential = await signInWithPopup(firebase.auth, provider);

    // Register with server to ensure user exists in database
    const idToken = await credential.user.getIdToken();
    await registerWithServer(idToken);

    return credential;
  } catch (error: any) {
    if (error.code === 'auth/account-exists-with-different-credential') {
      throw new Error(
        'An account with this email already exists. Please sign in with your password.',
      );
    }
    throw error;
  }
};

/**
 * Checks if the current user has access to this deployment
 *
 * @throws Error if network fails or server returns error
 *
 * @returns Promise resolving to CheckAccessResult with authorized status and user's deployments
 */
export const checkAccess = async (): Promise<CheckAccessResult> => {
  const firebase = getFirebase();
  const user = firebase.auth.currentUser;

  if (!user) {
    return { authorized: false, organizations: [] };
  }

  const idToken = await user.getIdToken();

  const response = await fetch('/api/auth/check-access', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to check access: ${response.statusText}`);
  }

  const data = await response.json();
  return {
    authorized: data.authorized,
    organizations: data.organizations || [],
    isAdmin: data.isAdmin ?? false,
  };
};

/**
 * Result of validating an invite
 */
export type ValidateInviteResult = {
  valid: boolean;
  reason?: string;
  deploymentId?: string;
  emailDomain?: string | null;
};

/**
 * Validates an invite link before account creation
 * This is a public endpoint - no auth required
 *
 * @param inviteId - The invite ID from the URL
 *
 * @returns Promise resolving to validation result
 */
export const validateInvite = async (
  inviteId: string,
): Promise<ValidateInviteResult> => {
  const response = await fetch('/api/auth/validate-invite', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ inviteId }),
  });

  if (!response.ok) {
    throw new Error(`Failed to validate invite: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Redeems an invite link to join an organization
 * Requires authentication
 *
 * @param inviteId - The invite ID to redeem
 *
 * @returns Promise resolving to redemption result
 */
export const redeemInvite = async (
  inviteId: string,
): Promise<{
  success: boolean;
  organizationId: string;
  message: string;
}> => {
  const firebase = getFirebase();
  const user = firebase.auth.currentUser;

  if (!user) {
    throw new Error('Must be logged in to redeem invite');
  }

  const idToken = await user.getIdToken();

  const response = await fetch('/api/auth/redeem-invite', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ inviteId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to redeem invite');
  }

  return response.json();
};
